/* eslint-disable no-undef */
import { AreaId, CityId, StateId } from 'types/Location';
import { Access, Activities, ClosedStatus, CompletedStatus, Difficulty, Features, RouteType, VisitorUsage } from '../Trails';
import { Sort } from './Sort';

type FilterFeatures = Extract<
  Features,
  | Features.BEACH
  | Features.CAVE
  | Features.CITY_WALK
  | Features.EVENT
  | Features.FOREST
  | Features.HISTORIC_SITE
  | Features.HOT_SPRINGS
  | Features.LAKE
  | Features.PUB_CRAWL
  | Features.RAILS_TRAILS
  | Features.RIVER
  | Features.VIEWS
  | Features.WATERFALL
  | Features.WILD_FLOWERS
  | Features.WILDLIFE
>;
type LinkedStatus = 'unlinked' | 'linked';

type FilterConfigArea = {
  name: string;
  popularity: number;
  selected: boolean;
  value: number;
};

type FilterConfigStarRating = string | null;

type FilterConfigToggleKey = Access | Activities | ClosedStatus | CompletedStatus | Difficulty | FilterFeatures | LinkedStatus | RouteType | Sort | VisitorUsage;

type FilterConfigToggleEntry = {
  name: string;
  selected: boolean;
  value: string | number | string[] | number[];
};

type FilterConfigToggle = Record<FilterConfigToggleKey, FilterConfigToggleEntry>;

type FilterConfigSlider = {
  initialRange: [number, number];
  stepIncreaseAt: number[];
  stepIncreaseBy: number[];
  upperBound: number;
  displayFunction: 'm' | 'km' | 'ft' | 'mi' | 'duration';
  multiplier: number;
  // The calculated data represented by the current indexes.
  value: [number, number];
  // The current selected indexes.
  displayValue: [number, number];
  // The human-presentable adjusted values based on the current index and step.
  formattedRange: [string, string];
};

type Filters = {
  closed_status:  Record<ClosedStatus, FilterConfigToggleEntry>;
  completed: Record<CompletedStatus, FilterConfigToggleEntry>;
  linked: Record<LinkedStatus, FilterConfigToggleEntry>;
  activities: Record<Activities, FilterConfigToggleEntry>;
  features: Record<FilterFeatures, FilterConfigToggleEntry>;
  access: Record<Access, FilterConfigToggleEntry>;
  states: Record<StateId, FilterConfigArea>;
  cities: Record<CityId, FilterConfigArea>;
  areas: Record<AreaId, FilterConfigArea>;
  difficulty: Record<Difficulty, FilterConfigToggleEntry>;
  route_type: Record<RouteType, FilterConfigToggleEntry>;
  visitor_usage: Record<VisitorUsage, FilterConfigToggleEntry>;
  lengths: FilterConfigSlider;
  elevation_gain: FilterConfigSlider;
  sort: Record<Sort, FilterConfigToggleEntry>;
  minRating: FilterConfigStarRating;

  // These are not always set intentionally by the user, and are not necessarily
  // filters in the traditional sense. "Filters" is an overloaded term that means
  // both the well-structured UI-driven object we define and anything that impacts
  // search results, even if it cannot and is not handled by our filtering UI.
  //
  // We may want to consider separating out:
  //   * user-defined knobs-and-dials
  //   * system-defined knobs-and-dials
  //   * user state that impacts search
  //
  // The ways we handle changes and use these fields vs user-defined fields are
  // not always the same.
  boundingBox: any | null;
  countries: any;
  has_profile_photo: boolean | null;
  isMinimumViableContent: boolean | null;
  initLocationObject: any | null;
  initialUserSlug: string | null;
  locationObject: any | null;
  mapIds: any[];
  mobileMap: boolean;
  queryTerm: string;
  trailIds: any[];
  trackIds: any[];
  zoomLevel: number | null;
};

export enum FilterType {
  ACCESS = 'access',
  ACTIVITIES = 'activities',
  DIFFICULTY = 'difficulty',
  ELEVATION_GAIN = 'elevation_gain',
  FEATURES = 'features',
  LENGTHS = 'lengths',
  MIN_RATING = 'minRating',
  ROUTE_TYPE = 'route_type',
  SORT = 'sort',
  SUITABILITY = 'access'
}

export enum FilterInput {
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
  SLIDER = 'slider',
  STAR_RATING = 'rating'
}

type FilterGeneric = {
  admin?: boolean;
  display: string;
  // Note: Had to add 'enabledKey' because the enabledFilters keys don't necessarily match the filter keys
  enabledKey?: string;
  extraProps?: any;
  key: string;
  nested?: boolean;
  truncate?: boolean;
  type: FilterInput;
};

type FilterCheckbox = FilterGeneric & {
  type: FilterInput.CHECKBOX;
  config: FilterConfigToggle;
};

type FilterRadio = FilterGeneric & {
  type: FilterInput.RADIO;
  config: FilterConfigToggle;
};

type FilterRangeUpdates = {
  convertedRange: [number, number];
  displayRange: [number, number];
  formattedRange: [string, string];
};

type FilterSlider = FilterGeneric & {
  type: FilterInput.SLIDER;
  config: FilterConfigSlider;
};

type FilterStarRating = FilterGeneric & {
  type: FilterInput.STAR_RATING;
  config: FilterConfigStarRating;
};

// Visually distinct filters. This type could use a better name.
type Filter = FilterCheckbox | FilterRadio | FilterSlider | FilterStarRating;

// The configuration (not including things like 'key', 'display', etc.) for a given type of filter logic that we support.
type FilterConfig = FilterConfigToggle | FilterConfigSlider | FilterConfigStarRating;

export {
  Filter,
  FilterCheckbox,
  FilterConfig,
  FilterConfigStarRating,
  FilterConfigSlider,
  FilterConfigToggle,
  FilterConfigToggleEntry,
  FilterConfigToggleKey,
  FilterRadio,
  FilterRangeUpdates,
  Filters,
  FilterStarRating
};
